/**
 * Non-concessional (after-tax) super contributions — who may make one, and how much.
 *
 * WHY THIS EXISTS. Until now the only route money could take into super in this tool was a downsizer
 * contribution: $300,000 per person, once, tied to a home sale. Everything above that could only
 * land in the bank, shares, an investment property, an annuity or a gift — all of which are counted
 * by Centrelink. That mattered most for the couple this tool sees constantly in the Retirement
 * Village module: one member under Age Pension age, whose accumulation super is exempt from BOTH
 * means tests until they reach it (see downsizerContributionUtils and
 * server/tests/rest/calculation/youngerSpouseSuperExemption.test.ts). The lever existed; there was
 * simply no way to put more than $300k behind it.
 *
 * ELIGIBILITY IS ALL-OR-NOTHING, BY DESIGN. The owner's instruction (2026-08-26) was that this be
 * logic-based: where a person cannot contribute, the strategy must not appear in Manual Control and
 * must not be weighted by the Suggestion Engine — not shown greyed out, not offered and then
 * refused. `nonConcessionalCapacityFor` returns 0 in exactly those cases, and every caller treats 0
 * as "this strategy does not exist for this person".
 *
 * THE RULES MODELLED (ATO, 2026-27 — verified 2026-08-26; re-check each 1 July):
 *  - Age: must be UNDER 75. No work test applies to non-concessional contributions.
 *  - Total super balance at the previous 30 June decides the cap, in bands set by the general
 *    transfer balance cap less one and two times the annual cap:
 *        TSB < $1.84m -> $390,000 (3-year bring-forward)
 *        TSB < $1.97m -> $260,000 (2-year bring-forward)
 *        TSB < $2.10m -> $130,000 (annual cap only)
 *        TSB >= $2.10m -> NIL — no contribution at all.
 *  - The cap is frozen at the first year's rate for a bring-forward period already running; this
 *    tool models a contribution made now, so it always prices the current year's rate.
 *
 * WHAT IS DELIBERATELY NOT MODELLED: concessional contributions (a work test applies at 67-74, the
 * cap is small, and for this cohort it rarely moves the answer), and the 28-day grace a fund may
 * allow after the month a person turns 75.
 */
import { applyDownsizerContributionToAssets, getOwnerAgeFromFactFind } from './downsizerContributionUtils.ts';
import {
    GENERAL_TRANSFER_BALANCE_CAP,
    NON_CONCESSIONAL_CONTRIBUTION_CAP,
    NON_CONCESSIONAL_MAX_AGE,
    PENSION_AGE,
} from './constants/ratesAndThresholds.ts';
import type { FactFind, FactFindAssetMap } from './factFindTypes.ts';

export type ContributionOwner = 'client' | 'partner';

/** Why a person cannot contribute — for the words the UI shows, never for a calculation. */
export type NonConcessionalBlocker = 'age' | 'total_super_balance' | 'no_person' | null;

export interface NonConcessionalCapacity {
    owner: ContributionOwner;
    /** The most this person may contribute right now. 0 means the strategy does not exist for them. */
    cap: number;
    /** Years of cap available: 3 or 2 under bring-forward, 1 for the annual cap alone, 0 for none. */
    years: number;
    /** Total super balance used to decide the band — derived unless the caller supplied one. */
    totalSuperBalance: number;
    /** True when `totalSuperBalance` was estimated from current balances rather than stated. */
    balanceEstimated: boolean;
    blocker: NonConcessionalBlocker;
    /** Their accumulation super is exempt from both means tests (they are under Age Pension age). */
    exemptFromMeansTests: boolean;
}

const amount = (value: unknown): number => {
    const parsed = parseFloat(String(value ?? ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Estimate a person's total super balance from what the fact-find already holds: accumulation super
 * plus account-based pensions in their name.
 *
 * This is an ESTIMATE and callers must say so. A real TSB is struck at the previous 30 June (not
 * today) and can include funds this tool never sees. It is good enough to decide whether the
 * question is worth asking; it is not good enough to quietly assume when the answer sits near a
 * band edge — which is why `nonConcessionalCapacityFor` reports `balanceEstimated`.
 */
export function estimateTotalSuperBalance(factFind: FactFind, owner: ContributionOwner): number {
    const assets = (factFind?.assets || {}) as Record<string, Array<Record<string, unknown>> | undefined>;
    let total = 0;
    for (const type of ['superannuation', 'accountBasedPensions']) {
        for (const asset of assets[type] || []) {
            if ((asset?.ownership as string) !== owner) continue;
            total += amount(asset?.value ?? (asset as Record<string, unknown>)?.balance);
        }
    }
    return total;
}

/**
 * How much `owner` may contribute after tax right now — 0 when they may not contribute at all.
 *
 * `statedTotalSuperBalance` is the figure the adviser confirmed (from the ATO's myGov record). Pass
 * it whenever it is known; without it the balance is estimated from the fact-find and flagged.
 */
export function nonConcessionalCapacityFor(
    factFind: FactFind,
    owner: ContributionOwner,
    statedTotalSuperBalance?: number | null,
): NonConcessionalCapacity {
    const isCouple = factFind?.adviceFor === 'couple';
    if (owner === 'partner' && !isCouple) {
        return { owner, cap: 0, years: 0, totalSuperBalance: 0, balanceEstimated: false, blocker: 'no_person', exemptFromMeansTests: false };
    }

    const age = getOwnerAgeFromFactFind(factFind, owner);
    if (age == null) {
        return { owner, cap: 0, years: 0, totalSuperBalance: 0, balanceEstimated: false, blocker: 'no_person', exemptFromMeansTests: false };
    }

    const balanceEstimated = statedTotalSuperBalance == null;
    const totalSuperBalance = balanceEstimated ? estimateTotalSuperBalance(factFind, owner) : Math.max(0, statedTotalSuperBalance as number);
    const exemptFromMeansTests = age < PENSION_AGE;

    // Age first: under 75 or nothing at all, whatever the balance says.
    if (age >= NON_CONCESSIONAL_MAX_AGE) {
        return { owner, cap: 0, years: 0, totalSuperBalance, balanceEstimated, blocker: 'age', exemptFromMeansTests };
    }

    // The bands ARE the transfer balance cap less one and two annual caps — derived, never hardcoded,
    // so both figures indexing on the same 1 July can only move them together.
    const twoYearFloor = GENERAL_TRANSFER_BALANCE_CAP - 2 * NON_CONCESSIONAL_CONTRIBUTION_CAP;
    const oneYearFloor = GENERAL_TRANSFER_BALANCE_CAP - NON_CONCESSIONAL_CONTRIBUTION_CAP;

    let years: number;
    if (totalSuperBalance >= GENERAL_TRANSFER_BALANCE_CAP) years = 0;
    else if (totalSuperBalance >= oneYearFloor) years = 1;
    else if (totalSuperBalance >= twoYearFloor) years = 2;
    else years = 3;

    return {
        owner,
        cap: years * NON_CONCESSIONAL_CONTRIBUTION_CAP,
        years,
        totalSuperBalance,
        balanceEstimated,
        blocker: years === 0 ? 'total_super_balance' : null,
        exemptFromMeansTests,
    };
}

/**
 * The one line a screen shows where a person has no after-tax contribution field — the reason, in
 * plain words, with the person's name (owner, 2 Oct 2026). Null where there is nothing to say.
 */
export function nonConcessionalBlockerWords(name: string, blocker: NonConcessionalBlocker): string | null {
    const who = name === 'you' ? 'You' : name;
    const cannot = name === 'you' ? "can't" : "can't";
    if (blocker === 'age') return `${who} ${cannot} make an after-tax contribution — the rules close the door at ${NON_CONCESSIONAL_MAX_AGE}.`;
    if (blocker === 'total_super_balance') return `${who} ${cannot} make an after-tax contribution — ${name === 'you' ? 'your' : 'their'} total super balance is already at the cap (${GENERAL_TRANSFER_BALANCE_CAP.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })}).`;
    return null;
}

export interface NonConcessionalHouseholdCapacity {
    client: NonConcessionalCapacity;
    partner: NonConcessionalCapacity;
    /** Combined room. 0 means the strategy does not exist for this household — show nothing. */
    total: number;
    /**
     * Who to fill first: the member whose accumulation super escapes both means tests, when exactly
     * one of the CONTRIBUTION-ELIGIBLE members does. Same principle as the downsizer allocation, and
     * the reason this strategy is worth offering at all.
     */
    preferred: ContributionOwner | null;
}

export function nonConcessionalCapacityForHousehold(
    factFind: FactFind,
    stated: { client?: number | null; partner?: number | null } = {},
): NonConcessionalHouseholdCapacity {
    const client = nonConcessionalCapacityFor(factFind, 'client', stated.client);
    const partner = nonConcessionalCapacityFor(factFind, 'partner', stated.partner);

    const clientPrefers = client.cap > 0 && client.exemptFromMeansTests;
    const partnerPrefers = partner.cap > 0 && partner.exemptFromMeansTests;
    const preferred: ContributionOwner | null = clientPrefers && !partnerPrefers ? 'client' : partnerPrefers && !clientPrefers ? 'partner' : null;

    return { client, partner, total: client.cap + partner.cap, preferred };
}

export interface NonConcessionalAllocation {
    client: number;
    partner: number;
    preferred: ContributionOwner | null;
}

/**
 * Split an after-tax contribution across the household, filling the means-test-exempt member first
 * and never exceeding either person's own cap. Mirrors allocateDownsizerContribution deliberately:
 * an adviser reading the two allocations side by side should see one rule, not two.
 */
export function allocateNonConcessionalContribution(
    factFind: FactFind,
    available: number,
    stated: { client?: number | null; partner?: number | null } = {},
): NonConcessionalAllocation {
    const capacity = nonConcessionalCapacityForHousehold(factFind, stated);
    let remaining = Math.max(0, available || 0);
    let client = 0;
    let partner = 0;

    const fill = (owner: ContributionOwner) => {
        const cap = owner === 'client' ? capacity.client.cap : capacity.partner.cap;
        const take = Math.min(cap, remaining);
        remaining -= take;
        if (owner === 'client') client = take;
        else partner = take;
    };

    if (capacity.preferred === 'client') {
        fill('client');
        fill('partner');
    } else if (capacity.preferred === 'partner') {
        fill('partner');
        fill('client');
    } else if (capacity.client.cap > 0 && capacity.partner.cap > 0) {
        // Pension-neutral: split evenly, which also spreads a later pension commencement across two
        // transfer balance caps instead of loading one.
        const half = Math.round(remaining / 2);
        client = Math.min(capacity.client.cap, half);
        remaining -= client;
        partner = Math.min(capacity.partner.cap, remaining);
    } else {
        fill('client');
        fill('partner');
    }

    return { client, partner, preferred: capacity.preferred };
}

export const NON_CONCESSIONAL_LABELS = {
    abp: 'After-tax contribution ABP',
    capped: 'After-tax contribution to super (pension cap reached)',
    contribution: 'After-tax super contribution',
} as const;

/**
 * Book an after-tax (non-concessional) contribution for `ownership` — F.6 of the actuarial review
 * pack, 17 Sep 2026. CAPPED at what that person may contribute right now (`nonConcessionalCapacityFor`):
 * a request above the cap books the cap and the caller keeps the rest, so no projection ever holds
 * a contribution the rules would refuse. Returns what was booked (0 where the person cannot
 * contribute at all). The money lands as accumulation super, or as an account-based pension where
 * `commenceIncomeStream` is set and the person may commence one — the same booking, and the same
 * transfer balance cap handling, as a downsizer contribution.
 */
export function applyNonConcessionalContributionToAssets(
    assets: FactFindAssetMap,
    factFind: FactFind,
    options: { amount: number; ownership: ContributionOwner; commenceIncomeStream?: boolean; idPrefix?: string; statedTotalSuperBalance?: number | null },
): number {
    const capacity = nonConcessionalCapacityFor(factFind, options.ownership, options.statedTotalSuperBalance);
    const amount = Math.min(Math.max(0, options.amount || 0), capacity.cap);
    if (amount <= 0) return 0;
    applyDownsizerContributionToAssets(assets, factFind, {
        amount,
        ownership: options.ownership,
        commenceIncomeStream: options.commenceIncomeStream === true,
        idPrefix: options.idPrefix || 'after-tax-super',
        labels: NON_CONCESSIONAL_LABELS,
    });
    return amount;
}
